import { X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { PAYMENTS_ACTIVITY_BASE_PATH } from "@/modules/dashboard/payments-activity/utils/activityLinks";
import { ONBOARDING_VERIFICATION_ROUTE } from "@/modules/verification/constants/verification.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import type {
  DispersionErrorKind,
  DispersionResultPhase,
  DispersionSuccessData,
} from "../../hooks/useSubmitDispersion";
import { PayoutStatus } from "../../services/paymentsPayout.service";
import type { EnhancedCountry } from "../../types/dispersions-api.types";
import { PayoutReceipt } from "./PayoutReceipt";

export interface DispersionResultScreensProps {
  phase: Exclude<DispersionResultPhase, "idle">;
  successData: DispersionSuccessData | null;
  /**
   * The payout's CURRENT status — the create response upgraded by the live channel (`usePayoutLiveStatus`).
   * Drives the whole outcome screen, so it never claims the money left before the provider says so.
   */
  payoutStatus: PayoutStatus | null;
  countries: EnhancedCountry[];
  submitError: string | null;
  showDetails: boolean;
  onToggleDetails: () => void;
  /** Receipt action: start the same send over — same recipient, same amount. */
  onSendAgain: () => void;
  /** Receipt action: same wallet, corridor and amount, recipient cleared. */
  onSendToSomeoneElse: () => void;
  /** Receipt action: leave for the wallets home. */
  onBackToWallets: () => void;
  /** Error action: new idempotency key + back to step 2 to edit the data. */
  onAdjustData: () => void;
  /** Error action: back to step 3 to retry the same operation (same idempotency key). */
  onRetry: () => void;
  /** Decides whether retrying is safe. Defaults to the harmless case. */
  errorKind?: DispersionErrorKind;
  /** Signs the comprobante while the organization has no identity of its own. */
  organizationName?: string | null;
  /**
   * The money on its way, drawn by the host that knows the route (the wallet send panel). Replaces the
   * spinner while the payout is created and the clock while the destination bank has not confirmed it.
   */
  transit?: ReactNode;
  /** Replaces the processing title, e.g. to say a conversion happens on the way. */
  processingTitle?: string;
}

/**
 * Processing / success / error screens of the wizard, rendered inside the page card while `resultPhase !==
 * "idle"`. This component is only the phase switch — the success phase is a receipt and lives in
 * `PayoutReceipt`, which owns the three outcomes a created payout can be in.
 *
 * Entrance animations come from `tailwindcss-animated` utilities. They used to reference `@keyframes` that a
 * `<style>` tag in `DispersionsListPage` injected at runtime, which meant these screens only animated while
 * that page happened to be mounted.
 */
export function DispersionResultScreens({
  phase,
  successData,
  payoutStatus,
  countries,
  submitError,
  showDetails,
  onToggleDetails,
  onSendAgain,
  onSendToSomeoneElse,
  onBackToWallets,
  onAdjustData,
  onRetry,
  errorKind = "generic",
  organizationName,
  transit,
  processingTitle,
}: DispersionResultScreensProps) {
  const { t } = useTranslations("dispersions");
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The button that was focused is unmounted when the phase flips, dropping focus to <body> — so a screen
  // reader announced nothing at all while a payout was being created. The success phase does its own
  // focusing, inside the receipt.
  useEffect(() => {
    if (phase === "error") headingRef.current?.focus();
  }, [phase]);

  if (phase === "processing") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-4 px-10 py-16 text-center"
      >
        {transit ?? (
          <div className="size-11 animate-spin rounded-full border-[3px] border-border border-t-primary" />
        )}
        <p className="text-lg font-semibold text-foreground">
          {processingTitle ?? t("result_processing_title")}
        </p>
        <p className="max-w-[320px] text-sm text-muted-foreground">{t("result_processing_subtitle")}</p>
      </div>
    );
  }

  if (phase === "success") {
    if (!successData) return null;
    return (
      <PayoutReceipt
        successData={successData}
        payoutStatus={payoutStatus}
        countries={countries}
        showDetails={showDetails}
        onToggleDetails={onToggleDetails}
        onSendAgain={onSendAgain}
        onSendToSomeoneElse={onSendToSomeoneElse}
        onBackToWallets={onBackToWallets}
        organizationName={organizationName}
        pendingVisual={transit}
      />
    );
  }

  // phase === "error"
  //
  // Retry is offered only when the attempt is known to have moved nothing. On a conflict the quote may have
  // been spent — meaning a payout may exist — and the button that used to sit here invited the operator to
  // pay twice. The history link replaces it, because the quote's own status cannot say whether the payout
  // went out, only that the quote is gone.
  const mayHavePaid = errorKind === "quote_consumed" || errorKind === "unconfirmed";
  // A verification refusal moved nothing either, but adjusting the data cannot fix it: the only exit is the
  // onboarding, so the action row leads there and nowhere else.
  const needsVerification = errorKind === "verification_required";

  return (
    <div className="flex animate-fade-up animate-duration-250 flex-col items-center gap-4 px-10 pt-12 pb-10 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <X className="size-7" strokeWidth={2.6} aria-hidden="true" />
      </div>
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-xl font-bold tracking-tight text-foreground outline-none"
      >
        {t("result_error_title")}
      </h2>
      <p className="max-w-[400px] text-sm leading-relaxed text-muted-foreground">
        {submitError || t("result_error_fallback")}
      </p>
      <div className="mt-1.5 flex gap-3">
        {!needsVerification && (
          <ColocaButton variant="default" size="lg" className="px-4" onClick={onAdjustData}>
            {t("result_error_adjust")}
          </ColocaButton>
        )}
        {needsVerification ? (
          <Link
            to={ONBOARDING_VERIFICATION_ROUTE}
            className="inline-flex h-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t("result_error_go_verify")}
          </Link>
        ) : mayHavePaid ? (
          // A real anchor, not a button that navigates: this is the one action that leads somewhere the
          // operator may want in another tab. `ColocaButton` does not re-expose `asChild`, so the styling is
          // borrowed rather than wrapped.
          <Link
            to={PAYMENTS_ACTIVITY_BASE_PATH}
            className="inline-flex h-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {t("result_error_check_activity")}
          </Link>
        ) : (
          <ColocaButton variant="primary" size="lg" className="px-4" onClick={onRetry}>
            {t("button_retry")}
          </ColocaButton>
        )}
      </div>
    </div>
  );
}
